import Alert from "@mui/material/Alert";
import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import * as bankAccountActions from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.actions";
import * as bankAccountSelectors from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.selectors";
import { getMessageForError } from "@rentiohq/shared-frontend/dist/utils/error.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Form,
  Box,
  Button,
  MainCard,
  Typography,
  Divider,
  Link,
  Stack,
  SpinningLoader,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import { FormikValues } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { addBankAccountSchema } from "../../../../forms/addBankAccount";
import {
  getAddBankAccountBackLink,
  getAddBankAccountSuccessPath,
} from "../../Payments.utils";
import { AddBankAccountStyles } from "./AddBankAccount.styles";

const AddBankAccountBase = () => {
  const sx = AddBankAccountStyles();

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state: IPartialRootState) =>
    authSelectors.getUser(state),
  );

  const isAddingIban = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.isCreatingContactBankAccount(state),
  );

  const createIbanError = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.createContactBankAccountError(state),
  );

  React.useEffect(() => {
    if (createIbanError) {
      enqueueSnackbar({
        // The V2 API carries its message on `exception.response.message`, not on
        // the legacy `error.message` this used to read, so every create failure
        // degraded to the generic text. getMessageForError knows both shapes and
        // falls back to a localized generic message when there is nothing to show.
        message: getMessageForError(createIbanError),
        variant: "error",
      });
    }
  }, [createIbanError]);

  // Generic, caller-supplied context - this component doesn't know or care
  // which feature is navigating here, only what it was told to show/return to.
  const locationState = location.state as
    | undefined
    | { referrer?: string; infoMessage?: string };

  const handleCustomSuccessAction = (ibanId?: number) => {
    if (!ibanId) {
      return;
    }

    navigate(
      getAddBankAccountSuccessPath({
        referrer: locationState?.referrer,
        ibanId,
      }),
    );
  };

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  // Back link and Cancel lead to the same page.
  const backLink = getAddBankAccountBackLink({
    paymentRequestId,
    referrer: locationState?.referrer,
  });

  const handleSubmit = ({ iban, ownerName }: FormikValues) => {
    dispatch(
      bankAccountActions.createContactBankAccountStart.getAction({
        contactId: user?.personalContactId,
        data: { iban, ownerName },
        onSuccess: id => handleCustomSuccessAction(id),
      }),
    );
  };

  const renderActions = () => (
    <Stack direction="row" gap={1} mt={2.5}>
      <Button
        variant={EButtonVariants.Outlined}
        fullWidth
        onClick={() => navigate(backLink.path)}
      >
        {getLocalizedText("system.cancel")}
      </Button>
      <Button variant={EButtonVariants.Contained} type="submit" fullWidth>
        {getLocalizedText("system.save")}
      </Button>
    </Stack>
  );

  return (
    <Box>
      <Link
        label={`< ${getLocalizedText(backLink.labelKey)}`}
        underlined={false}
        onClick={() => navigate(backLink.path)}
      />
      <Box mt={2}>
        <MainCard content={false}>
          <Box py={2} px={3}>
            <Typography variant="h4" sx={sx.content.heading}>
              {getLocalizedText("tenant.payments.add_bank.title")}
            </Typography>
            <Typography
              variant="caption"
              dangerouslySetInnerHTML={{
                __html: getLocalizedText("tenant.payments.add_bank.info"),
              }}
            />
          </Box>
          <Divider />

          <Box py={2} px={3} maxWidth={500}>
            {locationState?.infoMessage && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {locationState.infoMessage}
              </Alert>
            )}
            {isAddingIban ? (
              <SpinningLoader />
            ) : (
              <Form
                onSubmit={handleSubmit}
                getSchema={addBankAccountSchema({
                  ownerName: user ? getName(user) : undefined,
                  validateSepaSupported: true,
                })}
                customActions={renderActions()}
              />
            )}
          </Box>
        </MainCard>
      </Box>
    </Box>
  );
};

export const AddBankAccount = withKycRequired(AddBankAccountBase);
